import { useCallback } from "react";

import type { FileDropzoneProps } from "@skilitics/designer-ui-components";
import { FileDropzone } from "@skilitics/designer-ui-components";
import type { SlotId } from "@skilitics/nautilus-core-designer-data";

import { NodeDesigner } from "../NodeDesigner";
import type { MediaConfig } from "./types";
import { useAssetSlotDesigner } from "./useAssetSlotDesigner";
import { useSlotDesigner } from "./useSlotDesigner";

// note: the asset slot designer (asd) is aiming to be a simplified replacement for
//       the media slot designer (msd).

//       at the moment the msd provides some additional functionality which has not
//       been added to the asd but which may be added on an "as-needed" basis as
//       development continues.

//       these additions include: a property copying mechanism, callbacks for "onAdded"
//       and "onRemoved", and an upload confirmation dialog.

type AssetSlotDesignerProps = {
  slotId: SlotId;
  removeAction?: "removeAsset" | "removeResident";
  mimeType?: string;
  mediaTypes?: FileDropzoneProps["mediaTypes"];
  typeLabel?: string;
  dataTest?: string;
};

export const AssetSlotDesigner = ({
  slotId,
  removeAction = "removeResident",
  mediaTypes,
  mimeType,
  typeLabel,
  dataTest,
}: AssetSlotDesignerProps) => {
  const { resident } = useSlotDesigner(slotId);
  const { add, remove, update, cancelAdd } = useAssetSlotDesigner(slotId);

  const removeAsset = useCallback(
    () => update({ assetId: "", name: "", naturalWidth: undefined, naturalHeight: undefined } as MediaConfig),
    [update],
  );
  const handleFileDeselected = removeAction === "removeResident" ? remove : removeAsset;

  return (
    <FileDropzone
      mimeType={mimeType}
      mediaTypes={mediaTypes}
      typeLabel={typeLabel}
      onFileSelected={add}
      onFileDeselected={handleFileDeselected}
      onFileSelectionCancelled={cancelAdd}
      dataTest={dataTest}
    >
      {resident && resident.config?.assetId && <NodeDesigner node={resident} view="summary" />}
    </FileDropzone>
  );
};
